import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../auth/AuthContext'
import { usePrefs } from '../dashboard/usePrefs'
import { apiGet, apiPatch, apiPost } from '../../lib/api'
import { qk } from '../../lib/queryClient'
import { subscribeNotifBadge } from '../../lib/rt'
import type { NotifItem, NotifKind, Reminder } from '../../lib/types'
import { fmtDateTime, timeAgo } from '../common'
import './notifs.css'

// Campanita (notificaciones.md §Campanita + §Reminders).
// Badge por listener al doc padre (1 doc); las listas se piden a la API solo con
// el panel abierto. Vive en el DOCK hexagonal (anchor="dock"): el disparador es
// un hexágono con su badge y el popover abre HACIA ARRIBA. El modo "topbar" (el
// gemelo del toggle de tema, popover hacia abajo) se conserva por si se reubica.

type Tab = 'notifs' | 'reminders' | 'ajustes'

// Icono de campana reutilizado por ambos modos (dock/topbar).
const BellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
)

export function NotifBell({ anchor = 'topbar' }: { anchor?: 'topbar' | 'dock' }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [unread, setUnread] = useState(0)
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<Tab>('notifs')

  const email = user?.email
  useEffect(() => {
    if (!email) return
    return subscribeNotifBadge(email, setUnread)
  }, [email])

  if (!email) return null

  const toggle = () => {
    // Al abrir, refresca la bandeja: el badge en vivo pudo cambiar hace rato.
    if (!open) void qc.invalidateQueries({ queryKey: qk.notifs })
    setOpen(!open)
  }

  const goTo = (item: NotifItem) => {
    setOpen(false)
    navigate(item.link)
  }

  const isDock = anchor === 'dock'
  const badge = unread > 0 ? (unread > 9 ? '9+' : unread) : null

  return (
    <div className={isDock ? 'notif-bell bd-slot' : 'notif-bell'}>
      {isDock ? (
        // Modo dock: hexágono (gemelo de los del BeeDock) con el badge como
        // HERMANO del botón (el clip-path recortaría hijos). El tooltip ya no es
        // un hermano pintado a mano: lo pone la capa única de la app (data-tip).
        <>
          <button
            type="button"
            className={open ? 'bee-dock-hex active' : 'bee-dock-hex'}
            onClick={toggle}
            aria-expanded={open}
            aria-haspopup="dialog"
            aria-label={unread > 0 ? `Notificaciones (${unread} sin leer)` : 'Notificaciones'}
            data-tip="Notificaciones"
          >
            <span className="bd-ico"><BellIcon /></span>
          </button>
          {badge != null && <span className="bd-badge" aria-hidden="true">{badge}</span>}
        </>
      ) : (
        <button
          type="button"
          className="nb-btn"
          onClick={toggle}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={unread > 0 ? `Notificaciones (${unread} sin leer)` : 'Notificaciones'}
          title="Notificaciones"
        >
          <BellIcon />
          {badge != null && <span className="nb-badge">{badge}</span>}
        </button>
      )}

      {open && (
        <>
          <button
            type="button"
            className="nb-overlay"
            aria-label="Cerrar panel de notificaciones"
            onClick={() => setOpen(false)}
          />
          <div
            className={isDock ? 'nb-pop up pop' : 'nb-pop pop'}
            role="dialog"
            aria-label="Notificaciones y reminders"
          >
            <div className="nb-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'notifs'}
                className={tab === 'notifs' ? 'nb-tab active' : 'nb-tab'}
                onClick={() => setTab('notifs')}
              >
                Notificaciones
                {unread > 0 && <span className="nb-count">{unread > 99 ? '99+' : unread}</span>}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'reminders'}
                className={tab === 'reminders' ? 'nb-tab active' : 'nb-tab'}
                onClick={() => setTab('reminders')}
              >
                Reminders
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'ajustes'}
                className={tab === 'ajustes' ? 'nb-tab active' : 'nb-tab'}
                onClick={() => setTab('ajustes')}
              >
                Ajustes
              </button>
            </div>
            {tab === 'notifs' && <NotifList onGo={goTo} />}
            {tab === 'reminders' && <RemindersPanel />}
            {tab === 'ajustes' && <AjustesPanel />}
          </div>
        </>
      )}
    </div>
  )
}

// ── Pestaña Notificaciones ───────────────────────────────────────────────────

function NotifList({ onGo }: { onGo: (item: NotifItem) => void }) {
  const qc = useQueryClient()
  const { data: items, isLoading } = useQuery({
    queryKey: qk.notifs,
    queryFn: () => apiGet<{ items: NotifItem[] }>('/api/notifications'),
    select: (d) => d.items,
  })

  const markRead = useMutation({
    mutationFn: (body: { ids?: string[]; all?: true }) =>
      apiPost<{ ok: boolean }>('/api/notifications/read', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.notifs }),
  })

  const hasUnread = (items ?? []).some((n) => !n.read)

  const openItem = (item: NotifItem) => {
    if (!item.read) {
      // Optimista: se pinta leída de inmediato; el badge lo baja el listener.
      qc.setQueryData<{ items: NotifItem[] }>(qk.notifs, (old) =>
        old ? { items: old.items.map((n) => (n.id === item.id ? { ...n, read: true } : n)) } : old,
      )
      markRead.mutate({ ids: [item.id] })
    }
    onGo(item)
  }

  return (
    <div className="nb-body">
      <div className="nb-actions">
        <button
          type="button"
          className="btn btn-ghost nb-small"
          disabled={!hasUnread || markRead.isPending}
          onClick={() => markRead.mutate({ all: true })}
        >
          Marcar todo leído
        </button>
      </div>
      <div className="nb-list">
        {isLoading && <p className="nb-empty muted">Cargando…</p>}
        {!isLoading && (items ?? []).length === 0 && (
          <p className="nb-empty muted">Sin notificaciones por ahora.</p>
        )}
        {(items ?? []).map((item) => (
          <button
            type="button"
            key={item.id}
            className={item.read ? 'nb-item' : 'nb-item unread'}
            onClick={() => openItem(item)}
          >
            <span className="nb-ico" aria-hidden="true">
              <KindIcon kind={item.kind} />
            </span>
            <span className="nb-text">
              <span className="nb-title">{item.title}</span>
              {item.body && <span className="nb-snippet">{item.body}</span>}
              <span className="nb-when">{timeAgo(item.at)}</span>
            </span>
            {!item.read && <span className="nb-dot" aria-hidden="true" />}
          </button>
        ))}
      </div>
    </div>
  )
}

function KindIcon({ kind }: { kind: NotifKind }) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  } as const
  switch (kind) {
    case 'mention':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="4" />
          <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94" />
        </svg>
      )
    case 'assign':
      return (
        <svg {...common}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="8.5" cy="7" r="4" />
          <path d="M17 11l2 2 4-4" />
        </svg>
      )
    case 'chat':
      return (
        <svg {...common}>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      )
    case 'post':
      return (
        <svg {...common}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
          <path d="M16 13H8M16 17H8" />
        </svg>
      )
    case 'reminder':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 3" />
        </svg>
      )
    case 'project':
      return (
        <svg {...common}>
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      )
    case 'cell':
      return (
        <svg {...common}>
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        </svg>
      )
  }
}

// ── Pestaña Ajustes ──────────────────────────────────────────────────────────
// Único lugar donde se editan las preferencias de notificación (no hay página de
// perfil). Escribe con usePrefs: optimista + PUT con debounce, así que el switch
// responde al instante aunque la red esté lenta.

function AjustesPanel() {
  const { prefs, loading, error, save, recargar } = usePrefs()
  const np = prefs?.notifPrefs

  // digest ausente = prendido: los docs de prefs viejos no traen el campo y el
  // servidor los trata igual (routes/prefs.ts defaultPrefs).
  const digest = np?.digest !== false
  const push = np?.push !== false

  if (loading) return <p className="nb-empty muted">Cargando…</p>
  // Sin prefs no hay switches que pintar. Antes esto caía en el mismo "Cargando…"
  // y se quedaba ahí para siempre: react-query ya no reintenta solo.
  if (!np)
    return (
      <div className="nb-empty muted">
        <p>{error ? 'No se pudieron cargar tus preferencias.' : 'No hay preferencias que mostrar.'}</p>
        <button type="button" className="btn btn-ghost" onClick={() => void recargar()}>
          Reintentar
        </button>
      </div>
    )

  return (
    <div className="nb-body">
      <div className="nb-prefs">
        <p className="nb-sect muted">Resumen diario</p>
        <label className="nb-pref">
          <input
            type="checkbox"
            checked={digest}
            onChange={(e) => save({ notifPrefs: { ...np, digest: e.target.checked } })}
          />
          <span>
            Resumen diario de pendientes (8:30)
            <span className="nb-pref-hint">
              Una notificación cada mañana con lo vencido, lo de hoy y lo que viene.
            </span>
          </span>
        </label>

        <p className="nb-sect muted">Push</p>
        <label className="nb-pref">
          <input
            type="checkbox"
            checked={push}
            onChange={(e) => save({ notifPrefs: { ...np, push: e.target.checked } })}
          />
          <span>Notificaciones push en este dispositivo</span>
        </label>
        <label className={push ? 'nb-pref' : 'nb-pref off'}>
          <input
            type="checkbox"
            disabled={!push}
            checked={np.pushChat !== false}
            onChange={(e) => save({ notifPrefs: { ...np, pushChat: e.target.checked } })}
          />
          <span>Push de mensajes de chat</span>
        </label>
        <label className={push ? 'nb-pref' : 'nb-pref off'}>
          <input
            type="checkbox"
            disabled={!push}
            checked={np.pushMentionsOnly === true}
            onChange={(e) => save({ notifPrefs: { ...np, pushMentionsOnly: e.target.checked } })}
          />
          <span>Solo menciones y recordatorios</span>
        </label>
      </div>
    </div>
  )
}

// ── Pestaña Mis reminders ────────────────────────────────────────────────────

function RemindersPanel() {
  const qc = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)

  const { data: reminders, isLoading } = useQuery({
    queryKey: qk.reminders,
    queryFn: () => apiGet<{ reminders: Reminder[] }>('/api/reminders'),
    select: (d) => d.reminders,
  })

  const cancel = useMutation({
    mutationFn: (remId: string) =>
      apiPatch<{ reminder: Reminder }>(`/api/reminders/${remId}`, { status: 'cancelled' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.reminders }),
  })

  const pending = (reminders ?? []).filter((r) => r.status === 'pending')
  const fired = (reminders ?? []).filter((r) => r.status === 'fired')

  return (
    <div className="nb-body">
      <div className="nb-actions">
        <button
          type="button"
          className="btn btn-primary nb-small"
          onClick={() => setFormOpen((v) => !v)}
        >
          + Recordatorio
        </button>
      </div>

      {formOpen && <ReminderForm onDone={() => setFormOpen(false)} />}

      <div className="nb-list">
        {isLoading && <p className="nb-empty muted">Cargando…</p>}
        {!isLoading && pending.length === 0 && !formOpen && (
          <p className="nb-empty muted">No tienes reminders pendientes.</p>
        )}
        {pending.map((r) => (
          <div key={r.id} className="nb-rem">
            <span className="nb-text">
              <span className="nb-title">{r.message}</span>
              <span className="nb-when">{fmtDateTime(r.dueAt)}</span>
            </span>
            <button
              type="button"
              className="icon-btn danger nb-cancel"
              title="Cancelar reminder"
              aria-label={`Cancelar reminder: ${r.message}`}
              disabled={cancel.isPending}
              onClick={() => cancel.mutate(r.id)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
        {fired.length > 0 && (
          <>
            <p className="nb-sect muted">Disparados recientes</p>
            {fired.map((r) => (
              <div key={r.id} className="nb-rem done">
                <span className="nb-text">
                  <span className="nb-title">{r.message}</span>
                  <span className="nb-when">{fmtDateTime(r.dueAt)}</span>
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

// Presets de hora (notificaciones.md R18): capturados en hora LOCAL del navegador
// (MX en la práctica) y convertidos a ISO UTC justo al enviar.
type Preset = '1h' | '6pm' | 'manana' | 'custom'

function presetDate(p: Preset, customLocal: string): Date | null {
  const now = new Date()
  if (p === '1h') return new Date(now.getTime() + 3600_000)
  if (p === '6pm') {
    const d = new Date()
    d.setHours(18, 0, 0, 0)
    return d
  }
  if (p === 'manana') {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    d.setHours(9, 0, 0, 0)
    return d
  }
  if (!customLocal) return null
  const d = new Date(customLocal) // datetime-local se interpreta en hora local
  return Number.isNaN(d.getTime()) ? null : d
}

function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function ReminderForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient()
  const [message, setMessage] = useState('')
  const [preset, setPreset] = useState<Preset>('1h')
  const [custom, setCustom] = useState('')

  const create = useMutation({
    mutationFn: (body: { message: string; dueAt: string }) =>
      apiPost<{ reminder: Reminder }>('/api/reminders', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.reminders })
      onDone()
    },
  })

  const due = presetDate(preset, custom)
  const valid = message.trim().length > 0 && due != null && due.getTime() > Date.now()
  const sixPmPast = useMemo(() => {
    const d = new Date()
    d.setHours(18, 0, 0, 0)
    return d.getTime() <= Date.now()
  }, [])

  const presets: { key: Preset; label: string; disabled?: boolean }[] = [
    { key: '1h', label: 'En 1 h' },
    { key: '6pm', label: 'Hoy 6 pm', disabled: sixPmPast },
    { key: 'manana', label: 'Mañana 9 am' },
    { key: 'custom', label: 'Otra fecha' },
  ]

  return (
    <form
      className="nb-form"
      onSubmit={(e) => {
        e.preventDefault()
        if (!valid || !due) return
        create.mutate({ message: message.trim(), dueAt: due.toISOString() })
      }}
    >
      <input
        type="text"
        value={message}
        maxLength={200}
        placeholder="¿Qué te recordamos?"
        aria-label="Mensaje del recordatorio"
        onChange={(e) => setMessage(e.target.value)}
      />
      <div className="nb-presets" role="group" aria-label="Cuándo">
        {presets.map((p) => (
          <button
            key={p.key}
            type="button"
            className={preset === p.key ? 'nb-preset active' : 'nb-preset'}
            disabled={p.disabled}
            onClick={() => setPreset(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>
      {preset === 'custom' && (
        <input
          type="datetime-local"
          value={custom}
          min={toLocalInputValue(new Date())}
          aria-label="Fecha y hora del recordatorio"
          onChange={(e) => setCustom(e.target.value)}
        />
      )}
      {due != null && (
        <p className="nb-when muted">Sonará el {fmtDateTime(due.toISOString())}</p>
      )}
      {create.isError && (
        <p className="nb-error">No se pudo crear el reminder. Revisa la fecha e intenta de nuevo.</p>
      )}
      <div className="nb-form-actions">
        <button type="button" className="btn btn-ghost nb-small" onClick={onDone}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary nb-small" disabled={!valid || create.isPending}>
          {create.isPending ? 'Guardando…' : 'Crear'}
        </button>
      </div>
    </form>
  )
}
